import { BaseCard, BaseCardBody, BaseCardFooter } from "@components/Card/Card"
import Details from "@components/Details/Details"
import { MikanSpan } from "@components/Mikan"
import { PackageAccordionTrigger } from "@components/PricingCards/PricingCards"
import PricingEstimator from "@components/PricingEstimator/PricingEstimator"
import Hero from "@components/hero"
import RichText from "@components/rich-text/renderer"
import { Box, Container, Grid } from "@fastly/consistently"
import { Button, H4, H5, Tile } from "@fastly/consistently-vanilla"
import { useLocation } from "@reach/router"
import { findById } from "@utils/contentful"
import mapMicrocopySet from "@utils/mapMicrocopySet"
import { graphql, navigate } from "gatsby"
import queryString from "query-string"
import { Fragment, useEffect, useMemo, useState } from "react"
import styled, { css } from "styled-components"

import { TableWrapper } from "@components/PricingTable/PricingTable"
import { fastlyIoUrl } from "@utils/fastly-io"
import reducePageContent from "@utils/reducePageContent"

const packageTokens = css`
  --pricing-dark: var(--color-grey-70);
  --pricing-medium: var(--color-grey-20);
  --pricing-light: var(--color-grey-10);
  --network-services-dark: var(--color-red-70);
  --network-services-medium: var(--color-red-30);
  --network-services-light: var(--color-red-5);
  --security-dark: var(--color-blue-70);
  --security-medium: var(--color-blue-30);
  --security-light: var(--color-blue-5);
  --compute-dark: var(--color-green-70);
  --compute-medium: var(--color-green-30);
  --compute-light: var(--color-green-5);
  --observability-dark: var(--color-yellow-50);
  --observability-medium: var(--color-yellow-40);
  --observability-light: var(--color-yellow-5);
`

const PackageTabs = styled.div`
  display: flex;
  margin-top: var(--size-space-2xl);
  position: relative;
  box-sizing: border-box;
  position: sticky;
  top: 7.5em;
  background: white;
  z-index: 10;
  @media (max-width: 436px) {
    overflow-x: scroll;
    overflow-y: hidden;
    position: static;
  }
`

const PackageButton = styled.button`
  ${packageTokens}
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--size-space-sm);
  height: auto;
  border: 1px solid var(--color-grey-30);
  color: var(--color-grey-60);
  border-top: 1px solid var(--color-grey-30);
  border-left: 1px solid var(--color-grey-30);
  background: var(--white);
  cursor: pointer;

  @media (min-width: 768px) {
    padding: var(--size-space-md);
  }

  ${PackageTabs} &:not(:first-child) {
    border-left: none;
  }

  h3 {
    color: var(--color-grey-60);
    font-size: var(--font-size-md);

    @media (min-width: 768px) {
      font-size: var(--font-size-xl);
    }
  }

  &[data-active="true"] {
    font-weight: bold;
    box-shadow: var(--shadow-md);
    margin: -3px -1px;
    border: none;
    z-index: 1;
    border-bottom: 4px solid var(--pricing-dark);

    h3 {
      color: var(--color-grey-90);
    }
  }
`

const PackageLine = styled.span`
  ${packageTokens}
  height: 1px;
  width: 100vw;
  position: absolute;
  margin-left: -50vw;
  left: 50%;
  bottom: 0;
  background-color: var(--pricing-medium);
`
const PricingTableWrapper = styled(Box)`
  margin-block-end: var(--size-space-4xl);
  border: 1px solid var(--pricing-dark);

  @media (min-width: 768px) {
    && {
      border: none;
    }
  }
`

const TableMobileNav = styled.div`
  position: sticky;
  top: 4.5rem;
  background: var(--white);
  z-index: 1;

  ul {
    list-style: none;
    display: flex;
    width: 100%;
    padding: 0;
    margin: 0;
  }

  li {
    width: 33.33%;
    position: relative;
    &:nth-child(even) {
      border-right: 1px solid var(--color-grey-30);
      border-left: 1px solid var(--color-grey-30);
    }
  }

  button {
    width: 100%;
    display: inline-block;
    margin: 0;
    text-decoration: none;
    color: var(--color-grey-60);
    font-size: 1rem;
    line-height: 1;
    cursor: pointer;
    text-align: center;
    font-weight: 700;
    padding-block: var(--size-space-lg);
    border: none;

    background: var(--pricing-light);
    &::after {
      background-color: var(--pricing-dark);
    }

    &[data-active="true"] {
      background: var(--white);
      color: var(--color-grey-90);

      &::after {
        content: "";
        height: 2px;
        width: 100%;
        position: absolute;
        bottom: 0;
        left: 0;
      }
    }
  }

  @media (min-width: 768px) {
    display: none;
  }
`
const SmallDot = styled.img`
  position: absolute;
  z-index: -1;
  left: 0;
`

const Main = styled.main`
  position: relative;
`

const StyledWrapper = styled.div`
  position: relative;
  overflow: hidden;
`

const TrialCard = styled(BaseCard)`
  box-shadow: var(--shadow-sm);
  &:hover {
    box-shadow: var(--shadow-sm);
  }

  &.noshadow {
    box-shadow: none;
  }
  &.wide {
    @media (max-width: 768px) {
      grid-column: unset;
    }
  }
`

const TierButton = styled(Button)`
  border: 0;
`

const ServicesSection = styled.div`
  margin-block-start: var(--size-space-xl);

  .visible {
    display: block;
  }
  .hidden {
    display: none;
  }
  ${packageTokens}

  ${PackageAccordionTrigger} {
    padding-inline: var(--size-space-lg);
    border-bottom: 0;
    color: var(--pricing-dark);

    &::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      height: 2px;
      width: 0;
    }

    h5 {
      color: var(--pricing-dark);
      &::after {
        background: var(--pricing-dark);
      }
    }

    &[data-state="open"] {
      &::after {
        width: 100%;
      }
    }

    & svg {
      color: currentColor;
      display: none;
    }
  }
`

const ProductDetails = styled.div`
  margin-block: var(--size-space-md);

  & h4 {
    margin-top: var(--size-space-4xl);
  }
`

const PricingTiers = styled.div`
  display: grid;
  grid-template-rows: auto;
  grid-template-column: 1fr;
  align-items: flex-start;
  gap: var(--size-space-md);
  margin-bottom: var(--size-space-lg);

  @media (min-width: 960px) {
    grid-template-columns: repeat(3, 1fr);
  }

  // Usage and Packages tiers should maintain content height when Free is expanded
  & [data-expanded="true"] {
    height: fit-content;
  }
`

const PricingTier = styled.div`
  display: grid;
  gap: var(--size-space-md);
  transition: all 0.25s;

  @media (min-width: 960px) {
    gap: var(--size-space-md);
    grid-template-rows: auto auto 1fr;
    min-height: fit-content;
  }

  & hr {
    margin: 0.25rem 0 1rem 0 !important;
  }

  & div:nth-of-type(4) {
    grid-column: 1/4;
  }
`
const PricingTierTile = styled(Tile)`
  grid-column: auto;

  // Packages tier on mobile is last
  // Packages tier has a red border
  & :nth-of-type(4) {
    border: 2px solid var(--red);

    & > div div:nth-child(4) {
      padding-block-start: var(--size-space-md);
    }
  }

  @media (min-width: 960px) {
    // Custom Tier
    & :nth-of-type(4) {
      display: grid;
      grid-column: 1/4;
    }

    // Custom Tier content
    & :nth-of-type(4) > div {
      grid-template-columns: repeat(10, 1fr);
      grid-template-rows: repeat(2, auto);
      grid-column: 1 / 4;

      & div:nth-child(1) {
        grid-column: 2/5;
        grid-row: 1;
      }
      & div:nth-child(2) {
        grid-column: 1;
        justify-content: start;
      }
      & div:nth-child(3) {
        grid-column: 1/5;
      }
      & div:nth-child(4) {
        grid-column: 6/11;
        grid-row: 1;
        padding-block-start: 0;
      }
    }
  }

  & hr {
    margin: 0.25rem 0 1rem 0 !important;
  }
`

const tabList = ["network-services", "security", "compute", "observability"]

/**
 * Given a relative URL with an anchor element, return just the anchor.
 * @param {string} url
 * @returns string
 */
const getAnchorLink = (url) => {
  const regex = /^\/.*(#[\w-]*)/
  const parsedUrl = url?.match(regex)
  return parsedUrl ? parsedUrl[1] : url
}

const PricingPage = ({ data: { page, packagesLabelMicrocopy } }) => {
  const pageContent = reducePageContent(page)
  const pricingPackagesLabels = packagesLabelMicrocopy.items
  const initialActiveTab = pricingPackagesLabels[0].title
  const [activePackage, setActivePackage] = useState(initialActiveTab)
  const [activeColumn, setActiveColumn] = useState(0)
  const [showing, setShowing] = useState(0)
  const location = useLocation()
  const search = queryString.parse(location.search)
  const [expanded, setExpanded] = useState(false)

  const handleExpandedTier = () => setExpanded(!expanded)

  useEffect(() => {
    if (tabList.includes(search.product)) {
      setActivePackage(search.product)
      const index = tabList.findIndex((tab) => tab === search.product)
      setShowing(index)
    }
  }, [search.product])

  const pricingCards = useMemo(
    () =>
      pageContent["peHM0bjvCdxPqaYcG5h7I"].reduce((acc, card) => {
        acc.push(card)
        return acc
      }, []),
    [pageContent]
  )

  const hero = pageContent["CsbYQ7bI4CSmNgDsIznNF"]
  const pageMicrocopySet = pageContent["53dwCsZjsQg8f0DRZXKQ7u"]

  const pricingPackages = pageContent["5TrBs2QpeiT7rLogzpAOqm"]
  const pricingPackagesText = findById(pricingPackages, "7KY6B3pLKsddt5y5Lt4lQV")

  const tabs = pageContent["4BZQfF6TIlO9fKUT8CjBU0"]

  const selfServicePricingText = pageContent["XF7tb1NXmqSGlBzNE2FPu"]
  const selfServicePricingGroup = pageContent["22inoMgeUCRY64vNbU4aNr"]
  const tierSeeMoreBenefitsText = pageContent["5QXSDBSFfvNK5iPrvAmTAC"]

  const trialsDescriptionText = pageContent["5GYFRQnmEv9UmQDmDGlmiU"]

  const productCatalog = pageContent["Mxs5ETXXCMODeEJTrlYwn"]
  const perProductCatalog = {}
  perProductCatalog["network-services"] = findById(productCatalog, "6IA7tdBHKKZRiHm4dO8sTt")
  perProductCatalog["security"] = findById(productCatalog, "5cnQzFiWjV57jjjvMgxjAh")
  perProductCatalog["compute"] = findById(productCatalog, "4se5bjYvBSvnEmUzLyr3gs")

  const labels = mapMicrocopySet(pageMicrocopySet) //since we use this utility function on most pages, I extracted to a helper

  const regionsMicrocopySet = mapMicrocopySet(pageContent["3VRsFY2ZYafRYqToDzy8Vn"])
  const regionLabels = Object.entries(regionsMicrocopySet).reduce((regions, [key, value]) => {
    const [, regionName] = key.split("region-")
    return { ...regions, [regionName]: value }
  }, {})
  const pricingEstimatorMeta = {
    microcopy: {
      ...mapMicrocopySet(pageContent["2IqjRFf0wZwZqIZdACS9sl"]),
      ...mapMicrocopySet(pageContent["2HkNlncIVXsV7KiwbXiOXt"]),
    },
    ...regionLabels,
  }

  const bandwidthPricingNotes = pageContent["5sk95NloI9rqlt1nwexC6Q"]

  const packageSectionContentMap = {
    introText: 0,
    featureCards: 1,
    compareTable: 2,
    trialCards: 3,
    details: 4,
  }

  const mobilePackageTitles = [labels.basic, labels.starter, labels.advantage, labels.ultimate]

  return (
    <Main>
      <StyledWrapper>
        <Container background="var(--grey-20)">
          <Hero type="narrowText">
            <RichText body={hero.rtContent} mikan={hero.mikan} />
          </Hero>
        </Container>
      </StyledWrapper>

      {/* PRICING TIER CARDS */}
      <Container pb="4xl" overflow="visible" background="var(--grey-20)">
        <PricingTiers>
          {pricingCards.map((card) => (
            <PricingTierTile key={card.contentful_id} background="white" padding="lg" data-expanded={expanded}>
              <PricingTier gridGap={"sm"} sm={3}>
                {card.section_content.map((section, i) => {
                  if (section.__typename === "ContentfulButton") {
                    // We only want the anchor in the URL for packages to avoid a flash of text
                    const ctaURL = getAnchorLink(section.url)
                    // We need to use Reach Router to navigate to avoid a flash of
                    // bad styling on the signup page.
                    const onClick = (e) => {
                      e.preventDefault()
                      navigate(ctaURL)
                    }
                    return (
                      <Box
                        key={section.contentful_id}
                        display="flex"
                        flexDirection="column"
                        gridColumn={i === 0 ? "span 2" : "span 3"}
                        height="100%"
                        justifyContent="flex-end"
                      >
                        <TierButton url={ctaURL} onClick={onClick} key={section.contentful_id} full>
                          {section.buttonText}
                        </TierButton>
                      </Box>
                    )
                  }
                  if (section.__typename === "ContentfulImage") {
                    return (
                      <Box
                        key={section.contentful_id}
                        alignItems="start"
                        display="grid"
                        justifyContent="end"
                        width="100%"
                      >
                        <img src={fastlyIoUrl(section.content.file.url)} alt="" />
                      </Box>
                    )
                  }

                  return (
                    <Box key={section.contentful_id} gridColumn={i === 0 ? "span 2" : ""}>
                      <RichText
                        key={section.contentful_id}
                        body={section.rtContent}
                        renderOptions={{
                          listItemImage: "/g-static/icons/checkmark-green-circle-filled.svg",
                          collapsedListMax: card.contentful_id === "hKbvUfgSOhURjKwYK8jRf" ? 6 : undefined,
                          collapsedListButtonText: tierSeeMoreBenefitsText,
                          onClickOverride: handleExpandedTier,
                        }}
                      />
                    </Box>
                  )
                })}
              </PricingTier>
            </PricingTierTile>
          ))}
        </PricingTiers>
      </Container>

      {/* PACKAGE TABS */}
      <Container id="package-tabs" pt="5xl" pb="2xl" overflow="visible">
        <SmallDot src="/g-static/background/small-dot-pattern.svg" alt="" aria-hidden="true" />
        <RichText body={pricingPackagesText.rtContent} mikan={pricingPackagesText.mikan} />
        <PackageTabs role="tablist" aria-orientation="vertical">
          {pricingPackagesLabels.map(({ title, text, contentful_id }, i) => (
            <PackageButton
              key={contentful_id}
              onClick={() => {
                setActivePackage(title), setShowing(i), setActiveColumn(0)
              }}
              data-active={activePackage === title ? "true" : "false"}
              data-type-of-service={title}
              role="tab"
            >
              <h3>{text}</h3>
            </PackageButton>
          ))}
          <PackageLine data-type-of-service={activePackage} />
        </PackageTabs>

        {tabs.map((tab, i) => {
          return (
            <ServicesSection
              show={showing === i}
              key={i}
              role="tabpanel"
              className={showing === i ? "visible" : "hidden"}
            >
              {/* COMPARE TABLE */}
              {tab.section_content[packageSectionContentMap.compareTable]?.rtContent && (
                <PricingTableWrapper data-type-of-service={pricingPackagesLabels[i].title}>
                  {/* Mobile Nav Table */}
                  <TableMobileNav>
                    <ul>
                      {mobilePackageTitles
                        .filter((_, tabIndex) => pricingPackagesLabels[i].title === "network-services" || tabIndex > 0)
                        .map((tab, tabIndex) => (
                          <li key={mobilePackageTitles[tabIndex]}>
                            <button
                              data-active={activeColumn === tabIndex}
                              data-type-of-service={pricingPackagesLabels[i].title}
                              onClick={() => setActiveColumn(tabIndex)}
                            >
                              <MikanSpan text={tab} />
                            </button>
                          </li>
                        ))}
                    </ul>
                  </TableMobileNav>
                  {/* Desktop & Core of the table */}
                  <TableWrapper data-active-column={activeColumn} data-type-of-service={pricingPackagesLabels[i].title}>
                    <RichText
                      body={tab.section_content[packageSectionContentMap.compareTable].rtContent}
                      mikan={tab.section_content[packageSectionContentMap.compareTable].mikan}
                    />
                  </TableWrapper>
                </PricingTableWrapper>
              )}

              {/* (trial) CARDS */}
              {tab.section_content[packageSectionContentMap.trialCards].section_content?.length && (
                <Container overflow="visible">
                  <RichText body={trialsDescriptionText.rtContent} />
                  <Grid paddingTop="lg" md={2}>
                    {tab.section_content[packageSectionContentMap.trialCards].section_content.map((item) => {
                      return (
                        <TrialCard className={item.className} key={item.contentful_id}>
                          <BaseCardBody>
                            <H4>{item.header}</H4>
                            <RichText body={item.content} mikan={item.mikan} />
                          </BaseCardBody>
                          <BaseCardFooter>
                            <RichText body={item.footer} />
                          </BaseCardFooter>
                        </TrialCard>
                      )
                    })}
                  </Grid>
                </Container>
              )}
              <ProductDetails>
                {tab.section_content[packageSectionContentMap.details].section_content.map((detail) => (
                  <Fragment key={detail.contentful_id}>
                    {detail.__typename === "ContentfulText" ? <RichText body={detail.rtContent} /> : null}
                    {detail.__typename === "ContentfulTeaser" && detail.name !== "pricing-estimator" ? (
                      <Details summary={<H5 marginBlockEnd="none">{detail.header}</H5>} mt="4xl">
                        <RichText body={detail.content} />
                      </Details>
                    ) : null}
                  </Fragment>
                ))}
              </ProductDetails>
            </ServicesSection>
          )
        })}
      </Container>

      {/* Self-service pricing */}
      <Container pt="xl" pb="2xl" overflow="visible">
        <RichText body={selfServicePricingText.rtContent} />
        {selfServicePricingGroup.map((detail) => (
          <Fragment key={detail.contentful_id}>
            {detail.name === "full-site-delivery-pricing" ? (
              <Details summary={<H5 marginBlockEnd="none">{detail.header}</H5>} mt="4xl">
                <RichText body={detail.content} />
                <Box maxWidth={700}>
                  <RichText body={detail.footer} />
                  <PricingEstimator meta={pricingEstimatorMeta} />
                  <RichText
                    body={bandwidthPricingNotes.rtContent}
                    mikan={bandwidthPricingNotes.mikan}
                    key={bandwidthPricingNotes.contentful_id}
                  />
                </Box>
              </Details>
            ) : (
              <Details key={detail.contentful_id} summary={<H5 marginBlockEnd="none">{detail.header}</H5>} mt="4xl">
                <div id={detail.name}>
                  <RichText body={detail.content} />
                </div>
              </Details>
            )}
          </Fragment>
        ))}
      </Container>
    </Main>
  )
}

export const query = graphql`
  query ($contentful_id: String!) {
    page: contentfulPage(contentful_id: { eq: $contentful_id }) {
      ...Page
    }
    packagesLabelMicrocopy: contentfulMicrocopySet(contentful_id: { eq: "1Jq7hkQ3kgfJ5Jz7zQapfB" }) {
      id
      items {
        text
        title
      }
    }
  }
`

export default PricingPage

export { Head } from "@components/Head/Head"
